import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import type { CatalogCardType, HorizontalCatalogCardType, PreviewCatalogCardType } from './Cards';

import CatalogVisibilityWrapper from './CatalogVisibilityWrapper';

type Props = {
    CardComponent?: CatalogCardType | HorizontalCatalogCardType | PreviewCatalogCardType;
    catalogCardClass?: string;
    catalogIds: number[];
    className?: string;
    horizontal?: boolean;
    lazyLoadImages?: boolean;
    previewSellerId?: number;
    testid?: string;
    trackClick?: (id: number) => void;
};

const CatalogGridComponent = ({
    CardComponent,
    catalogCardClass = 'catalog-card',
    catalogIds,
    className,
    horizontal = false,
    lazyLoadImages,
    testid,
    trackClick,
}: Props) => {
    const mapCatalogCards = useCallback(
        (catalogId: number, index: number) => {
            const cardProps: any = {
                catalogId,
                horizontal,
                lazyLoadImages: lazyLoadImages || index >= 6,
                position: index + 1,
                trackClick,
            };

            if (CardComponent) {
                return (
                    <CardComponent
                        className={catalogCardClass}
                        key={`${catalogId}-catalog-grid-card`}
                        {...cardProps}
                    />
                );
            }

            return (
                <div
                    className={cn(
                        'w-full max-w-full flex-[1_1_auto] px-xs pb-sm transition-all duration-500 [@media(min-width:600px)]:w-1/2 [@media(min-width:600px)]:max-w-[50%] [@media(min-width:986px)]:w-1/3 [@media(min-width:986px)]:max-w-[calc(100%/3)]',
                        catalogCardClass
                    )}
                    data-testid="catalogCardWrapper"
                    key={`${catalogId}-catalog-grid-card`}
                >
                    <CatalogVisibilityWrapper {...cardProps} />
                </div>
            );
        },
        [catalogCardClass, horizontal, lazyLoadImages, CardComponent, trackClick]
    );

    return (
        <div
            className={cn('flex flex-row flex-wrap content-stretch items-stretch justify-start', className)}
            data-testid={testid}
        >
            {catalogIds.map(mapCatalogCards)}
        </div>
    );
};

export const CatalogGrid = memo<Props>(CatalogGridComponent, isEqual);
